import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateStrategicVolume, useStrategicVolumes, useUpdateStrategicVolume } from '../hooks/api/strategicVolumes';
import { useCookieLocation, useLocationsByLoggedInUserRole } from '../hooks';
import CRUDSettings from '../components/General/CRUDSettings';
import StrategicVolumeDialog from '../components/StrategicVolume/StrategicVolumeDialog';
import WorkingUnitLocationSelect from '../components/Selects/WorkingUnitLocationSelect';
import { getCellValue } from '../utils/agGridUtils';

const StrategicVolumeSettings = () => {
    const { t } = useTranslation('bidding');
    const [locationOptions] = useLocationsByLoggedInUserRole();
    const [selectedLocation, setSelectedLocation] = useCookieLocation(locationOptions);
    const [selectedWorkingUnitLocation, setSelectedWorkingUnitLocation] = useState('');

    const [selectedStrategicVolume, setSelectedStrategicVolume] = useState(null);
    const [showCreateStrategicVolumeDialog, setShowCreateStrategicVolumeDialog] = useState(false);

    const [strategicVolumes] = useStrategicVolumes({ workingUnitLocation: selectedWorkingUnitLocation });
    const { mutate: handleCreateStrategicVolume } = useCreateStrategicVolume({
        workingUnitLocation: selectedWorkingUnitLocation,
        onSettled: () => {
            setShowCreateStrategicVolumeDialog(false);
        },
    });
    const { mutate: handleUpdateStrategicVolume } = useUpdateStrategicVolume({
        workingUnitLocation: selectedWorkingUnitLocation,
        onSettled: () => {
            setShowCreateStrategicVolumeDialog(false);
        },
    });

    const handlePost = (data) => {
        const { id, workingUnitLocation, capacityType, forecasters, ...rest } = data;

        // Transform forecasters based on source (existing forecaster vs new user)
        const forecasterId = forecasters.map((f) => {
            if (f.source === 'forecaster') {
                // Existing forecaster - send forecaster ID
                return { forecasterId: f.id };
            } else {
                // New user - send user ID and name to create forecaster
                return { userId: f.id, name: f.name };
            }
        });

        const strategicVolume = {
            ...(id && { id }),
            workingUnitLocationId: workingUnitLocation.id,
            capacityTypeId: capacityType,
            forecasterId,
            ...rest,
        };

        if (id) {
            handleUpdateStrategicVolume(strategicVolume);
            return;
        }

        handleCreateStrategicVolume(strategicVolume);
    };

    const columnDefs = [
        {
            field: 'location',
            headerName: t('field.location'),
            cellRenderer: (params) => getCellValue(params, 'city', 'code'),
            filterValueGetter: (params) => {
                return `${params.data.location.code} - ${params.data.location.city}`;
            },
        },
        {
            field: 'workingUnitLocation.workingUnit.worktype.worktype',
            headerName: t('field.worktype'),
        },
        {
            field: 'workingUnitLocation.workingUnit.channel.name',
            headerName: t('field.channel'),
        },
        {
            field: 'workingUnitLocation.workingUnit.task.name',
            headerName: t('field.task'),
        },
        {
            field: 'capacityType.name',
            headerName: t('field.capacityType'),
        },
        {
            field: 'capacityType.unit',
            headerName: t('field.unit'),
        },
        {
            field: 'forecasterNames',
            headerName: t('field.forecaster'),
            minWidth: 110,
        },
        {
            field: 'contact',
            headerName: t('field.contactType'),
            editable: true,
        },
        {
            field: 'comment',
            headerName: t('field.note'),
        },
    ];

    const filters = () => (
        <WorkingUnitLocationSelect
            locationOptions={locationOptions}
            selectedLocation={selectedLocation}
            onSelectedLocationChange={setSelectedLocation}
            selectedWorkingUnitLocation={selectedWorkingUnitLocation}
            onSelectedWorkingUnitLocationChange={setSelectedWorkingUnitLocation}
        ></WorkingUnitLocationSelect>
    );

    return (
        <>
            <CRUDSettings
                tableRows={strategicVolumes}
                columnDefs={columnDefs}
                columnToFocusOnEdit="location"
                btnText={t('sv.addNew')}
                recordType="Strategic Volume"
                pagination={true}
                paginationPageSize={10}
                paginationPageSizeSelector={[10, 20, 50, 100]}
                isCreationViaDialog={true}
                isEditViaDialog={true}
                setShowCreationDialog={setShowCreateStrategicVolumeDialog}
                onSelectedRowChange={(selectedRow) => setSelectedStrategicVolume(selectedRow)}
                filters={filters}
            ></CRUDSettings>
            {showCreateStrategicVolumeDialog && (
                <StrategicVolumeDialog
                    showDialog={showCreateStrategicVolumeDialog}
                    strategicVolume={selectedStrategicVolume}
                    onClose={() => setShowCreateStrategicVolumeDialog(false)}
                    onSubmit={handlePost}
                ></StrategicVolumeDialog>
            )}
        </>
    );
};

export default StrategicVolumeSettings;
